import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import FixedSidebar from '../../components/ui/FixedSidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import CandidateSummaryCard from './components/CandidateSummaryCard';
import CandidateTabContent from './components/CandidateTabContent';
import ActionPanel from './components/ActionPanel';
import CandidateTimeline from './components/CandidateTimeline';
import AIInsightsPanel from './components/AIInsightsPanel';
import Icon from '../../components/AppIcon';

const CandidateProfileDetail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Add isLoading state back since it was removed
  const [isLoading, setIsLoading] = useState(true);
  const [candidate, setCandidate] = useState(null);

  const candidateId = searchParams?.get('id') || '1';

  // Mock candidate data with enhanced AI data structure
  const mockCandidate = {
    id: candidateId,
    name: "Sarah Johnson",
    position: "Senior Frontend Developer",
    status: "interview-ready",
    aiScore: 87,
    skillsMatch: 92,
    experienceMatch: 85,
    educationMatch: 78,
    culturalFit: 88,
    email: "sarah.johnson@email.com",
    phone: "+1 (555) 123-4567",
    location: "San Francisco, CA",
    linkedin: "https://linkedin.com/in/sarahjohnson",
    appliedDate: "Dec 15, 2024",
    experience: "5+ years",
    salaryExpected: "$120,000 - $140,000",
    noticePeriod: "2 weeks",
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
    analysisDate: "Dec 16, 2024",
    ranking: "#3",
    matchPercentage: 87,
    responseTime: "2 days",
    pipelineStage: "technical",
    
    keyHighlights: [
      "5+ years of React development experience",
      "Led frontend team of 8 developers",
      "Expert in TypeScript and modern JavaScript",
      "Strong background in UI/UX design principles",
      "Experience with microservices architecture"
    ],
    
    keySkills: [
      "React", "TypeScript", "JavaScript", "Node.js", "GraphQL", 
      "AWS", "Docker", "Jest", "Cypress", "Figma", "Git", "Agile"
    ],
    
    aiSummary: `Sarah is an exceptional frontend developer with strong technical skills and leadership experience. Her expertise in React ecosystem and modern development practices makes her an excellent fit for senior-level positions. The AI analysis indicates high compatibility with our technical requirements and company culture. Her portfolio demonstrates consistent delivery of high-quality user interfaces and her communication skills are well-suited for collaborative environments.`,
    
    scoringBreakdown: [
      {
        category: "Technical Skills",
        score: 92,
        feedback: "Excellent proficiency in required technologies with advanced knowledge of React ecosystem and modern development practices."
      },
      {
        category: "Experience Level",
        score: 85,
        feedback: "Strong relevant experience with progressive responsibility and leadership roles in similar positions."
      },
      {
        category: "Education & Certifications",
        score: 78,
        feedback: "Good educational background with relevant certifications. Could benefit from additional cloud certifications."
      },
      {
        category: "Cultural Alignment",
        score: 88,
        feedback: "High compatibility with company values and work culture based on interview responses and background analysis."
      }
    ],
    
    recommendations: [
      {
        type: "positive",
        title: "Strong Technical Fit",
        description: "Candidate demonstrates excellent technical skills matching job requirements with room for growth."
      },
      {
        type: "positive",
        title: "Leadership Potential",
        description: "Previous team lead experience indicates strong potential for senior roles and mentoring capabilities."
      },
      {
        type: "caution",
        title: "Salary Expectations",
        description: "Salary expectations are at the higher end of the range. Consider negotiation strategies."
      }
    ],
    
    interviewNotes: [
      {
        interviewer: {
          name: "Michael Chen",
          avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face"
        },
        date: "Dec 18, 2024",
        type: "Technical Interview",
        rating: 4,
        feedback: `Sarah demonstrated excellent technical knowledge during the coding session. She solved complex problems efficiently and showed good understanding of system design principles. Her approach to problem-solving is methodical and well-structured. Communication was clear and she asked thoughtful questions about the role and team dynamics.`,
        tags: ["technical-strong", "problem-solving", "communication"]
      },
      {
        interviewer: {
          name: "Lisa Rodriguez",
          avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face"
        },
        date: "Dec 16, 2024",
        type: "Initial Screening",
        rating: 5,
        feedback: `Excellent first impression. Sarah is articulate, professional, and shows genuine enthusiasm for the role. Her experience aligns well with our requirements and she demonstrated good cultural fit during our conversation. She asked insightful questions about the team structure and growth opportunities.`,
        tags: ["cultural-fit", "enthusiasm", "professional"]
      }
    ],
    
    communications: [
      {
        type: "email",
        subject: "Interview Confirmation - Technical Round",
        content: "Thank you for confirming your availability for the technical interview. Please find the meeting details and preparation materials attached.",
        from: "hr@company.com",
        to: "sarah.johnson@email.com",
        date: "Dec 17, 2024",
        status: "delivered"
      },
      {
        type: "call",
        subject: "Initial Phone Screening",
        content: "30-minute phone screening to discuss background, experience, and role expectations. Candidate was responsive and engaged throughout the conversation.",
        from: "Lisa Rodriguez",
        to: "sarah.johnson@email.com",
        date: "Dec 15, 2024",
        status: "completed"
      },
      {
        type: "message",
        subject: "Application Acknowledgment",
        content: "Thank you for your application for the Senior Frontend Developer position. We have received your CV and will review it shortly.",
        from: "hr@company.com",
        to: "sarah.johnson@email.com",
        date: "Dec 14, 2024",
        status: "sent"
      }
    ],
    
    recentNotes: [
      {
        author: "Michael Chen",
        date: "Dec 18, 2024",
        content: "Strong technical performance in coding interview. Recommended for next round."
      },
      {
        author: "Lisa Rodriguez",
        date: "Dec 16, 2024",
        content: "Excellent cultural fit and communication skills. Very positive first impression."
      },
      {
        author: "AI System",
        date: "Dec 15, 2024",
        content: "OpenAI analysis completed with 87% match score. Candidate flagged for priority review."
      }
    ]
  };

  const mockTimeline = [
    {
      type: "application",
      title: "Application Submitted",
      description: "Candidate submitted application with CV and cover letter",
      date: "Dec 14, 2024",
      time: "2:30 PM",
      status: "completed",
      responsible: {
        name: "System",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face",
        role: "Automated"
      }
    },
    {
      type: "screening",
      title: "OpenAI Analysis Completed",
      description: "Automated CV analysis using GPT-4o model",
      date: "Dec 15, 2024",
      time: "9:15 AM",
      status: "completed",
      details: "OpenAI GPT-4o analysis completed with 87% match score. Candidate meets all technical requirements and shows strong potential for the role. Analysis included skills assessment, experience evaluation, and cultural fit prediction.",
      responsible: {
        name: "OpenAI GPT-4o",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face",
        role: "AI Analysis System"
      }
    },
    {
      type: "communication",
      title: "Initial Contact",
      description: "Phone screening scheduled and confirmation sent",
      date: "Dec 15, 2024",
      time: "11:45 AM",
      status: "completed",
      responsible: {
        name: "Lisa Rodriguez",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face",
        role: "HR Coordinator"
      }
    },
    {
      type: "interview",
      title: "Phone Screening",
      description: "Initial 30-minute phone interview completed",
      date: "Dec 16, 2024",
      time: "3:00 PM",
      status: "completed",
      details: "Candidate showed excellent communication skills and cultural fit. Recommended for technical interview round.",
      responsible: {
        name: "Lisa Rodriguez",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face",
        role: "HR Coordinator"
      }
    },
    {
      type: "status-change",
      title: "Status Updated",
      description: "Candidate status changed to \'Interview Ready'",
      date: "Dec 16, 2024",
      time: "4:30 PM",
      status: "completed",
      responsible: {
        name: "Lisa Rodriguez",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face",
        role: "HR Coordinator"
      }
    },
    {
      type: "interview",
      title: "Technical Interview",
      description: "Technical assessment and coding interview",
      date: "Dec 18, 2024",
      time: "10:00 AM",
      status: "completed",
      details: "Strong technical performance with excellent problem-solving skills. Candidate demonstrated proficiency in required technologies.",
      responsible: {
        name: "Michael Chen",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face",
        role: "Senior Developer"
      },
      attachments: [
        { name: "Technical Assessment Results.pdf", url: "#" },
        { name: "Code Review Notes.md", url: "#" }
      ]
    },
    {
      type: "note",
      title: "Internal Discussion",
      description: "Team discussion about candidate progression",
      date: "Dec 18, 2024",
      time: "2:15 PM",
      status: "completed",
      details: "Team consensus to proceed with final interview round. All interviewers provided positive feedback.",
      responsible: {
        name: "David Kim",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face",
        role: "Engineering Manager"
      }
    }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setCandidate(mockCandidate);
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [candidateId]);

  const handleStatusUpdate = (newStatus) => {
    setCandidate(prev => ({ ...prev, status: newStatus }));
    // Here you would typically make an API call to update the status
  };

  const handleScheduleInterview = () => {
    navigate('/interview-scheduling?candidate=' + candidateId);
  };

  const handleSendMessage = () => {
    // Handle send message logic
    console.log('Send message to candidate');
  };

  const customBreadcrumbs = [
    { label: 'Home', path: '/hr-dashboard', icon: 'Home' },
    { label: 'Candidates', path: '/candidate-database', icon: 'Users' },
    { label: candidate?.name || 'Loading...', path: `/candidate-profile-detail?id=${candidateId}`, icon: 'User', isLast: true }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          currentUser={{ name: "HR Manager", role: "Senior Recruiter" }}
          notificationCounts={{ total: 12, candidates: 5, processing: 3, interviews: 4 }}
        />
        <FixedSidebar currentPath={window.location?.pathname} />
        <main className="ml-72 pt-16">
          <div className="p-6">
            <div className="flex items-center justify-center min-h-96">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin" />
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Loading Candidate Profile</h2>
                <p className="text-muted-foreground">Please wait while we fetch the candidate details...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          currentUser={{ name: "HR Manager", role: "Senior Recruiter" }}
          notificationCounts={{ total: 12, candidates: 5, processing: 3, interviews: 4 }}
        />
        <FixedSidebar currentPath={window.location?.pathname} />
        <main className="ml-72 pt-16">
          <div className="p-6">
            <div className="flex items-center justify-center min-h-96">
              <div className="text-center">
                <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Candidate Not Found</h2>
                <p className="text-muted-foreground mb-4">The requested candidate profile could not be found.</p>
                <button
                  onClick={() => navigate('/candidate-database')}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Back to Candidates
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        currentUser={{ name: "HR Manager", role: "Senior Recruiter" }}
        notificationCounts={{ total: 12, candidates: 5, processing: 3, interviews: 4 }}
      />
      <FixedSidebar currentPath={window.location?.pathname} />
      <main className="ml-72 pt-16">
        <div className="p-6">
          <Breadcrumb customBreadcrumbs={customBreadcrumbs} />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Candidate Profile</h1>
              <p className="text-muted-foreground">
                Comprehensive view of candidate information with OpenAI-powered analysis insights
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/candidate-database')}
                className="flex items-center space-x-2 px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors micro-feedback"
              >
                <Icon name="ArrowLeft" size={16} />
                <span>Back to Candidates</span>
              </button>
            </div>
          </div>

          {/* Main Content Grid - Enhanced with AI Insights */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column - Candidate Summary */}
            <div className="lg:col-span-3">
              <CandidateSummaryCard candidate={candidate} />
            </div>

            {/* Center Column - Tabbed Content */}
            <div className="lg:col-span-6">
              <CandidateTabContent candidate={candidate} />
              
              {/* Timeline Section */}
              <div className="mt-6">
                <CandidateTimeline timeline={mockTimeline} />
              </div>
            </div>

            {/* Right Column - Enhanced with AI Insights */}
            <div className="lg:col-span-3 space-y-6">
              <ActionPanel 
                candidate={candidate}
                onStatusUpdate={handleStatusUpdate}
                onScheduleInterview={handleScheduleInterview}
                onSendMessage={handleSendMessage}
              />
              
              {/* AI Insights Panel */}
              <AIInsightsPanel 
                candidate={candidate}
                interviewNotes={candidate?.interviewNotes || []}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CandidateProfileDetail;